import { useCallback, useState } from "react";

import {
  AiInterviewQuestion,
  SelectedSuggestionInput,
  useApplyGuideQuestionSuggestionsMutation,
} from "../../graphql";
import { useAiInterviewerContext } from "./AiInterviewerContext";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

type UseApplyQuestionSuggestionsResult = {
  applySuggestions: (
    questionIndex: number,
    suggestions: SelectedSuggestionInput[]
  ) => Promise<AiInterviewQuestion | null>;
  loading: boolean;
  error: string | null;
};

const useApplyQuestionSuggestions = (): UseApplyQuestionSuggestionsResult => {
  const aiInterviewer = useAiInterviewerContext();
  const { getValues } = useAiInterviewerFormContext();
  const [error, setError] = useState<string | null>(null);

  const [applyMutation, { loading }] =
    useApplyGuideQuestionSuggestionsMutation();

  const applySuggestions = useCallback(
    async (
      questionIndex: number,
      suggestions: SelectedSuggestionInput[]
    ): Promise<AiInterviewQuestion | null> => {
      setError(null);
      try {
        const formValues = getValues();
        const currentQuestion = formValues.questions[questionIndex];

        if (!currentQuestion) {
          throw new Error("Question not found");
        }

        // Strip fields not needed for input
        const { sortOrder, __typename, originalQuestionId, ...questionInput } =
          currentQuestion;

        const result = await applyMutation({
          variables: {
            aiInterviewerId: aiInterviewer.id,
            questionIndex,
            currentQuestion: questionInput,
            selectedSuggestions: suggestions,
          },
        });

        return (
          result.data?.applyGuideQuestionSuggestions?.aiInterviewQuestion ??
          null
        );
      } catch (e) {
        const message =
          e instanceof Error ? e.message : "Failed to apply suggestions";
        setError(message);
        return null;
      }
    },
    [aiInterviewer.id, applyMutation, getValues]
  );

  return {
    applySuggestions,
    loading,
    error,
  };
};

export default useApplyQuestionSuggestions;
